// Real HTTP adapter for FEATURE-018/019/020 (Medical Staff List / Invite / Edit·Withdraw) — talks to
// admin-api at /api/v1/admin/medical-staff. Selected over the in-memory mock when VITE_USE_MOCK_API=false
// (see medical-staff.adapter.ts); the production/docker env files set that flag, so this is the live path.
// Maps the server error envelope { error: { code, message, fields, meta } } to MedicalStaffApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  MedicalStaffApiError,
  type AddOptionInput,
  type ExportMedicalStaffParams,
  type InviteMedicalStaffInput,
  type ListMedicalStaffParams,
  type ListMedicalStaffResult,
  type MedicalStaff,
  type MedicalStaffApi,
  type MedicalStaffApiErrorCode,
  type MedicalStaffOptions,
  type UpdateMedicalStaffInput,
} from '../types/medical-staff.types';

interface ApiEnvelope<T> {
  data: T;
}

// Wire shape from admin-api: the self-withdraw request is a FLAT timestamp string
// (`withdrawRequestedAt`), not the nested `withdrawRequest` object the UI uses.
type MedicalStaffWire = Omit<MedicalStaff, 'withdrawRequest'> & {
  withdrawRequestedAt?: string | null;
};

// Map one wire row → the UI's MedicalStaff: lift the flat `withdrawRequestedAt`
// into the nested `withdrawRequest` object (null when no pending request).
function toMedicalStaff(wire: MedicalStaffWire): MedicalStaff {
  const { withdrawRequestedAt, ...rest } = wire;
  return {
    ...rest,
    withdrawRequest: withdrawRequestedAt ? { requestedAt: withdrawRequestedAt } : null,
  };
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new MedicalStaffApiError(
    (payload?.code as MedicalStaffApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const medicalStaffHttpApi: MedicalStaffApi = {
  async listMedicalStaff(params: ListMedicalStaffParams): Promise<ListMedicalStaffResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<Omit<ListMedicalStaffResult, 'items'> & {
        items: MedicalStaffWire[];
      }>>(
        endpoints.medicalStaff,
        { params },
      );
      const result = res.data?.data;
      if (!result || typeof result !== 'object' || !Array.isArray(result.items)) {
        throw new MedicalStaffApiError('UNKNOWN', '의료진 목록을 불러올 수 없습니다.');
      }
      return { ...result, items: result.items.map(toMedicalStaff) };
    } catch (e) {
      mapError(e);
    }
  },

  async exportMedicalStaff(params: ExportMedicalStaffParams): Promise<MedicalStaff[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<MedicalStaffWire[]>>(
        `${endpoints.medicalStaff}/export`,
        { params },
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new MedicalStaffApiError('UNKNOWN', '의료진 목록을 불러올 수 없습니다.');
      }
      return data.map(toMedicalStaff);
    } catch (e) {
      mapError(e);
    }
  },

  async inviteMedicalStaff(input: InviteMedicalStaffInput): Promise<MedicalStaff> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffWire>>(
        `${endpoints.medicalStaff}/invitations`,
        input,
      );
      return toMedicalStaff(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async resendInvitation(id: number): Promise<void> {
    try {
      await httpClient.post(`${endpoints.medicalStaff}/${id}/resend-invitation`);
    } catch (e) {
      mapError(e);
    }
  },

  async updateMedicalStaff(id: number, input: UpdateMedicalStaffInput): Promise<MedicalStaff> {
    try {
      const res = await httpClient.patch<ApiEnvelope<MedicalStaffWire>>(
        `${endpoints.medicalStaff}/${id}`,
        input,
      );
      return toMedicalStaff(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async withdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffWire>>(
        `${endpoints.medicalStaff}/${id}/withdraw`,
      );
      return toMedicalStaff(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  // PRD 03 §3.8 (v1.3): approve a staff-initiated self-withdraw request → WITHDRAWN.
  async approveWithdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffWire>>(
        `${endpoints.medicalStaff}/${id}/withdraw-approve`,
      );
      return toMedicalStaff(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  // PRD 03 §3.8 (v1.3): reject a staff-initiated self-withdraw request (status unchanged).
  async rejectWithdrawMedicalStaff(id: number): Promise<MedicalStaff> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffWire>>(
        `${endpoints.medicalStaff}/${id}/withdraw-reject`,
      );
      return toMedicalStaff(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async resetPassword(id: number): Promise<void> {
    try {
      await httpClient.post(`${endpoints.medicalStaff}/${id}/reset-password`);
    } catch (e) {
      mapError(e);
    }
  },

  async listOptions(): Promise<MedicalStaffOptions> {
    try {
      const res = await httpClient.get<ApiEnvelope<MedicalStaffOptions>>(
        `${endpoints.medicalStaff}/options`,
      );
      const data = res.data?.data;
      if (!data || !Array.isArray(data.departments) || !Array.isArray(data.jobRoles)) {
        throw new MedicalStaffApiError('UNKNOWN', '옵션 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async addDepartmentOption(input: AddOptionInput): Promise<MedicalStaffOptions> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffOptions>>(
        `${endpoints.medicalStaff}/options/departments`,
        input,
      );
      const data = res.data?.data;
      if (!data || !Array.isArray(data.departments) || !Array.isArray(data.jobRoles)) {
        throw new MedicalStaffApiError('UNKNOWN', '옵션 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async addJobRoleOption(input: AddOptionInput): Promise<MedicalStaffOptions> {
    try {
      const res = await httpClient.post<ApiEnvelope<MedicalStaffOptions>>(
        `${endpoints.medicalStaff}/options/job-roles`,
        input,
      );
      const data = res.data?.data;
      if (!data || !Array.isArray(data.departments) || !Array.isArray(data.jobRoles)) {
        throw new MedicalStaffApiError('UNKNOWN', '옵션 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },
};
